import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import trackService from '../services/trackService';
import type { Track, Course } from '../services/trackService';
import axios from 'axios';

/**
 * Track details page component
 */
const TrackDetails: React.FC = () => {
  const { trackId } = useParams<{ trackId: string }>();
  const navigate = useNavigate();
  
  const [track, setTrack] = useState<Track | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchTrackDetails = async () => {
      if (!trackId) {
        setError('Track ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch track details
        const trackData = await trackService.getTrack(trackId);
        setTrack(trackData);
        
        // Fetch courses for this track
        const coursesData = await trackService.getTrackCourses(trackId);
        setCourses(coursesData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching track details:', err);
        if (axios.isAxiosError(err) && err.response) {
          setError(`Failed to load track details: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
        } else {
          setError('Failed to load track details. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTrackDetails();
  }, [trackId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleBack = () => {
    navigate('/tracks');
  };

  const handleRemoveCourse = async (courseId: string) => {
    if (!trackId) return;
    
    if (window.confirm('Are you sure you want to remove this course from the track?')) {
      try {
        await trackService.removeCourseFromTrack(trackId, courseId);
        setCourses(courses.filter(course => course.courseId !== courseId));
      } catch (err) {
        console.error('Error removing course:', err);
        alert('Failed to remove course. Please try again.');
      }
    }
  };

  // Filter courses based on search term
  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Format currency for display (defined for future use)
  const formatCurrency = (amount: number | string) => {
    // Ensure amount is a number
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Check if the conversion resulted in a valid number
    if (isNaN(numericAmount)) {
      return '$0.00'; // Default value if conversion fails
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numericAmount);
  };

  return (
    <div className="page-container">
      <div className="flex items-center mb-md">
        <button 
          className="btn btn-text"
          onClick={handleBack}
          style={{ marginRight: '16px' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Tracks
        </button>
        <h1 className="page-title">Track Details</h1>
      </div>
      
      {loading ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center p-lg">
            <div className="spinner"></div>
            <p className="mt-md">Loading track details...</p>
          </div>
        </div>
      ) : error ? (
        <div className="card">
          <div className="bg-error p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
            <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>Error!</h2>
            <p className="text-white">{error}</p>
          </div>
        </div>
      ) : track ? (
        <>
          {/* Track Info Card removed */}
          
          {/* Courses Card */}
          <div className="card">
            <div className="flex justify-between items-center mb-lg">
              <div className="flex items-center">
                <h2 className="text-xl font-bold mr-md">Courses</h2>
                <span className="tag bg-primary text-white">{courses.length}</span>
              </div>
              
              <div className="flex items-center">
                <input 
                  type="text" 
                  placeholder="Search courses..." 
                  className="p-sm mr-md"
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{ 
                    width: '250px',
                    borderRadius: 'var(--border-radius-sm)'
                  }}
                />
                
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate(`/tracks/${trackId}/add-courses`)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Add Courses
                </button>
              </div>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                    <th>LMS Platform ID</th>
                    <th>Last Synced</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCourses.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center p-md">
                        {searchTerm ? 'No courses match your search criteria' : 'No courses found in this track. Click "Add Courses" to add some.'}
                      </td>
                    </tr>
                  ) : (
                    filteredCourses.map((course) => (
                      <tr key={course.courseId}>
                        <td>{course.title}</td>
                        <td>{course.description || '-'}</td>
                        <td>{course.lmsPlatformId}</td>
                        <td>{new Date(course.syncedDate).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="btn btn-text"
                            onClick={() => handleRemoveCourse(course.courseId)}
                            title="Remove Course"
                            style={{ color: 'var(--color-error)' }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="card">
          <p className="text-center p-md">Track not found</p>
        </div>
      )}
    </div>
  );
};

export default TrackDetails;
